import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const Home = forwardRef((props, ref) => {
  const [displayText, setDisplayText] = useState('');
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  const phrases = [
    "Turning complex data into actionable business intelligence.",
    "Building interactive Power BI & Tableau dashboards.",
    "SQL querying, statistical modeling & data storytelling.",
    "Uncovering hidden trends to drive strategic decisions."
  ];

  useEffect(() => {
    const currentPhrase = phrases[phraseIndex];
    const typingSpeed = isDeleting ? 30 : 50;

    const timer = setTimeout(() => {
      if (!isDeleting) {
        if (displayText.length < currentPhrase.length) {
          setDisplayText(currentPhrase.slice(0, displayText.length + 1));
        } else {
          // Pause at end of phrase
          setTimeout(() => setIsDeleting(true), 1800);
        }
      } else {
        if (displayText.length > 0) {
          setDisplayText(currentPhrase.slice(0, displayText.length - 1));
        } else {
          setIsDeleting(false);
          setPhraseIndex((prev) => (prev + 1) % phrases.length);
        }
      }
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [displayText, isDeleting, phraseIndex]);

  useImperativeHandle(ref, () => ({
    restartAnimation: () => {
      setDisplayText('');
      setIsDeleting(false);
      setPhraseIndex(0);
    }
  }));

  return (
    <div className="home-container">
      {/* Animated Background particles */}
      <div className="animated-background">
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
      </div>

      <div className="split-layout">
        {/* Left Section - Hero Content */}
        <div className="left-section">
          <div className="home-text">
            <div className="welcome-message">
              <span className="greeting">
                <i className="fas fa-chart-line"></i> Data Analyst & BI Specialist
              </span>
              <h1 className="name">Monil Shah</h1>
              <div className="typing-container">
                <h2 className="role">{displayText}</h2>
                <span className="cursor">|</span>
              </div>
            </div>

            <p className="description">
              Passionate about transforming messy datasets into clear, executive-ready insights. 
              Proficient in <strong>Power BI</strong>, <strong>Tableau</strong>, <strong>SQL</strong>, <strong>Python</strong>, 
              and <strong>predictive modeling</strong> to uncover trends, optimize operations, and guide strategic business decisions.
            </p>

            <div className="domain-pills">
              <span className="domain-pill"><i className="fas fa-landmark"></i> Banking & Finance</span>
              <span className="domain-pill"><i className="fas fa-shopping-cart"></i> Retail Analytics</span>
              <span className="domain-pill"><i className="fas fa-globe-americas"></i> Demographics</span>
              <span className="domain-pill"><i className="fas fa-brain"></i> Predictive Risk</span>
            </div>

            <div className="home-buttons">
              <Link to="/projects" className="primary-btn">
                <span>Explore Dashboards</span>
                <i className="fas fa-chart-pie"></i>
              </Link>
              <Link to="/skills" className="secondary-btn">
                <span>Technical Skills</span>
                <i className="fas fa-layer-group"></i>
              </Link>
            </div>
          </div>
        </div>

        {/* Right Section - Executive KPI / Analytics Dashboard Widget */}
        <div className="right-section">
          <div className="profile-card">
            <div className="card-content">
              {/* Top Status Bar */}
              <div className="card-top-bar">
                <div className="live-status-pill">
                  <span className="pulse-dot"></span>
                  <span>Analytics Profile</span>
                </div>
                <span className="role-chip">BI & Insights</span>
              </div>

              {/* Social Links */}
              <div className="social-links">
                <a 
                  href="https://github.com/monilshah1409" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-icon"
                  aria-label="GitHub Profile"
                  title="GitHub"
                >
                  <i className="fab fa-github"></i>
                </a>
                <a 
                  href="https://linkedin.com/in/monil-shah-943a10258" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-icon"
                  aria-label="LinkedIn Profile"
                  title="LinkedIn"
                >
                  <i className="fab fa-linkedin"></i>
                </a>
                <a 
                  href="mailto:monilshah1409@gmail.com" 
                  className="social-icon"
                  aria-label="Email Me"
                  title="Email"
                >
                  <i className="far fa-envelope"></i>
                </a>
              </div>

              {/* Quick Stats */}
              <div className="quick-stats">
                <div className="stat">
                  <i className="fas fa-chart-line"></i>
                  <span className="stat-number">5+</span>
                  <span className="stat-label">Projects</span>
                </div>

                <div className="stat">
                  <i className="fas fa-certificate"></i>
                  <span className="stat-number">15+</span>
                  <span className="stat-label">Certifications</span>
                </div>

                <div className="stat">
                  <i className="fas fa-briefcase"></i>
                  <span className="stat-number">4</span>
                  <span className="stat-label">Internships</span>
                </div>
              </div>

              {/* Core Analytical Toolkit */}
              <div className="tech-stack">
                <div className="tech-stack-header">
                  <i className="fas fa-tools"></i>
                  <h3>Core Analytical Stack</h3>
                </div>
                <div className="tech-icons">
                  <div className="tech-item">
                    <div className="tech-icon-wrapper">
                      <i className="fas fa-chart-bar"></i>
                    </div>
                    <span className="tech-label">Power BI & Tableau</span>
                  </div>
                  <div className="tech-item">
                    <div className="tech-icon-wrapper">
                      <i className="fas fa-database"></i>
                    </div>
                    <span className="tech-label">Advanced SQL</span>
                  </div>
                  <div className="tech-item">
                    <div className="tech-icon-wrapper">
                      <i className="fas fa-chart-line"></i>
                    </div>
                    <span className="tech-label">Python & Scikit</span>
                  </div>
                  <div className="tech-item">
                    <div className="tech-icon-wrapper">
                      <i className="fas fa-file-excel"></i>
                    </div>
                    <span className="tech-label">Excel & DAX</span>
                  </div>
                  <div className="tech-item">
                    <div className="tech-icon-wrapper">
                      <i className="fas fa-filter"></i>
                    </div>
                    <span className="tech-label">ETL & Wrangling</span>
                  </div>
                  <div className="tech-item">
                    <div className="tech-icon-wrapper">
                      <i className="fas fa-lightbulb"></i>
                    </div>
                    <span className="tech-label">KPI Strategy</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="scroll-indicator">
        <div className="mouse">
          <div className="wheel"></div>
        </div>
        <span className="scroll-text">Scroll Down</span>
      </div>
    </div>
  );
});

export default Home;
